import moment from 'moment'
import StockService from '@/services/StockService'

// Lignes d'une même commande magasin (carton compris) ou d'un même bon de livraison manuel ; une réception reste seule
const isSameGroup = (entry, other) => {
  if (entry.type === 'shipment') {
    return other.type === 'shipment' && other.rayon === entry.rayon && other.id_shipment === entry.id_shipment
  }
  if (entry.type === 'delivery_note' && entry.bl_file) {
    return other.type === 'delivery_note' && other.bl_file === entry.bl_file
  }
  return other.id === entry.id
}

// Une commande magasin entière prend le lieu de la commande, produit par produit un lieu par produit ;
// un bon de livraison manuel ou une réception change de lieu ligne par ligne
const saveRelocation = ({ entry, wholeLocationId, moves, updatedBy }) => {
  if (entry.type === 'shipment' && wholeLocationId) {
    return StockService.setShipmentLocation(entry.rayon, entry.id_shipment, wholeLocationId, updatedBy)
  }
  if (entry.type === 'shipment') {
    const lines = moves.map(move => ({ productName: move.line.product_name, locationId: move.locationId }))
    return StockService.setShipmentProductLocations(entry.rayon, entry.id_shipment, lines, updatedBy)
  }
  return Promise.all(moves
    .filter(move => move.locationId !== move.line.location_id)
    .map(move => StockService.setMovementLocation(move.line.id, move.locationId, updatedBy)))
}

export default {
  state: {
    stockRows: [],
    stockLoader: false,
    stockLoaded: false,
    orders: [],
    ordersLoader: false,
    journal: [],
    journalLoader: false,
    categories: [],
    locations: [],
  },

  mutations: {
    setStockRows(state, rows) {
      state.stockRows = rows
      state.stockLoaded = true
    },
    setStockLoader(state, bool) {
      state.stockLoader = bool
    },
    setStockThreshold(state, { productName, alertThreshold }) {
      state.stockRows = state.stockRows.map(row =>
        row.product_name === productName ? { ...row, alert_threshold: alertThreshold } : row)
    },
    setStockTracked(state, { productName, tracked }) {
      state.stockRows = state.stockRows.map(row =>
        row.product_name === productName ? { ...row, tracked } : row)
    },
    setStockDetails(state, product) {
      state.stockRows = state.stockRows.map(row =>
        row.product_name === product.product_name
          ? {
            ...row,
            category: product.category,
            lead_time_days: product.lead_time_days,
            order_contacts: product.order_contacts,
            order_url: product.order_url,
            order_notes: product.order_notes
          }
          : row)
    },
    setCategories(state, categories) {
      state.categories = categories
    },
    setLocations(state, locations) {
      state.locations = locations
    },
    setOrders(state, orders) {
      state.orders = orders
    },
    setOrdersLoader(state, bool) {
      state.ordersLoader = bool
    },
    upsertOrder(state, order) {
      const exists = state.orders.some(existing => existing.id === order.id)
      state.orders = exists
        ? state.orders.map(existing => existing.id === order.id ? order : existing)
        : [order, ...state.orders]
    },
    setJournal(state, entries) {
      state.journal = entries
    },
    setJournalLoader(state, bool) {
      state.journalLoader = bool
    },
  },

  actions: {
    loadCurrentState({ commit, state }, { force = false } = {}) {
      if (state.stockLoaded && !force) return Promise.resolve()
      commit('setStockLoader', true)
      return StockService.getCurrentState()
        .then(response => {
          commit('setStockRows', Array.isArray(response) ? response : (response.data || []))
        })
        .finally(() => commit('setStockLoader', false))
    },

    syncStockProducts({ commit }) {
      commit('setStockLoader', true)
      return StockService.syncProducts()
        .then(() => StockService.getCurrentState())
        .then(response => {
          commit('setStockRows', Array.isArray(response) ? response : (response.data || []))
        })
        .finally(() => commit('setStockLoader', false))
    },

    addStockMovement({ dispatch }, movement) {
      return StockService.addMovement(movement)
        .then(() => dispatch('loadCurrentState', { force: true }))
    },

    // Inventaire par lieu : tout est enregistré en une transaction côté inventory, puis le stock est rechargé
    recordInventory({ dispatch }, { lines, createdBy }) {
      return StockService.recordInventory({ lines, createdBy })
        .finally(() => dispatch('loadCurrentState', { force: true }))
    },

    setStockThreshold({ commit }, { productName, alertThreshold }) {
      return StockService.setThreshold(productName, alertThreshold)
        .then(() => commit('setStockThreshold', { productName, alertThreshold }))
    },

    setStockTracked({ commit }, { productName, tracked }) {
      return StockService.setTracked(productName, tracked)
        .then(() => commit('setStockTracked', { productName, tracked }))
    },

    setStockDetails({ commit }, { productName, details }) {
      return StockService.setDetails(productName, details)
        .then(product => commit('setStockDetails', product))
    },

    loadLocations({ commit, state }) {
      if (state.locations.length) return Promise.resolve()
      return StockService.getLocations()
        .then(locations => commit('setLocations', Array.isArray(locations) ? locations : []))
    },

    transferStock({ dispatch }, transfer) {
      return StockService.transfer(transfer)
        .then(() => dispatch('loadCurrentState', { force: true }))
    },

    recordDeliveryNote({ dispatch }, deliveryNote) {
      return StockService.recordDeliveryNote(deliveryNote)
        .then(() => dispatch('loadCurrentState', { force: true }))
    },

    // moves = [{ line, locationId }] : chaque ligne du journal et son nouveau lieu ; wholeLocationId quand toute
    // la commande ou tout le bon prend le même lieu, null sinon
    relocateMovement({ dispatch }, relocation) {
      return saveRelocation(relocation).then(() => dispatch('loadCurrentState', { force: true }))
    },

    // Toutes les lignes de la commande magasin ou du bon de livraison d'un mouvement, lues sur les jours qui
    // l'entourent et sans filtre produit : le journal affiché peut ne montrer qu'un produit
    loadMovementGroup(_context, entry) {
      const day = moment(entry.date)
      return StockService.getJournal({
        startDate: day.clone().subtract(1, 'day').format('YYYY-MM-DD'),
        endDate: day.clone().add(1, 'day').format('YYYY-MM-DD')
      }).then(entries => entries.filter(other => isSameGroup(entry, other)))
    },

    loadCategories({ commit, state }) {
      if (state.categories.length) return Promise.resolve()
      return StockService.getCategories()
        .then(categories => commit('setCategories', Array.isArray(categories) ? categories : []))
    },

    loadOrders({ commit }) {
      commit('setOrdersLoader', true)
      return StockService.getOrders()
        .then(orders => commit('setOrders', Array.isArray(orders) ? orders : []))
        .finally(() => commit('setOrdersLoader', false))
    },

    createOrder({ commit }, payload) {
      return StockService.createOrder(payload)
        .then(order => {
          commit('upsertOrder', order)
          return order
        })
    },

    updateOrder({ commit, dispatch }, { orderId, payload }) {
      return StockService.updateOrder(orderId, payload)
        .then(order => {
          commit('upsertOrder', order)
          // l'annulation libère l'en-commande affiché sur l'onglet Stock
          return dispatch('loadCurrentState', { force: true }).then(() => order)
        })
    },

    receiveOrder({ commit, dispatch }, { orderId, receivedBy, lines, locationId }) {
      return StockService.receiveOrder(orderId, receivedBy, lines, locationId)
        .then(order => {
          commit('upsertOrder', order)
          return dispatch('loadCurrentState', { force: true }).then(() => order)
        })
    },

    loadJournal({ commit }, filters = {}) {
      commit('setJournalLoader', true)
      return StockService.getJournal(filters)
        .then(entries => commit('setJournal', Array.isArray(entries) ? entries : []))
        .finally(() => commit('setJournalLoader', false))
    },
  },
}
